import { useState } from "react";
import { ShoppingBag, Hexagon, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { AnimatePresence, motion } from "framer-motion";

const LINKS = [
  { href: "#features", label: "Features", testId: "nav-features" },
  { href: "#how", label: "How It Works", testId: "nav-how" },
  { href: "#viewer", label: "3D View", testId: "nav-viewer" },
  { href: "#specs", label: "Specs", testId: "nav-specs" },
  { href: "#buy", label: "Buy", testId: "nav-buy" },
];

const Nav = () => {
  const { cart, setOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10"
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 h-16 flex items-center justify-between">
        <a href="#hero" className="flex items-center gap-2 group" data-testid="nav-logo">
          <Hexagon className="w-5 h-5 text-[#007AFF] group-hover:rotate-90 transition-transform duration-500" strokeWidth={1.6} />
          <span className="font-heading text-2xl tracking-wider">GRIPFORCE<span className="text-[#007AFF]">X</span></span>
        </a>

        <nav className="hidden lg:flex items-center gap-10 text-xs uppercase tracking-[0.2em] text-white/70 font-body">
          {LINKS.map((l) => (
            <a key={l.href} href={l.href} className="hover:text-white transition" data-testid={l.testId}>{l.label}</a>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 px-3 sm:px-4 py-2 border border-white/15 hover:border-[#007AFF] hover:bg-[#007AFF]/10 transition"
            data-testid="nav-cart-button"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-4 h-4" strokeWidth={1.6} />
            <span className="hidden sm:inline text-xs uppercase tracking-[0.2em] font-body">Cart</span>
            {cart.count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#007AFF] text-black text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count-badge">
                {cart.count}
              </span>
            )}
          </button>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden p-2 border border-white/15 hover:border-[#007AFF] transition"
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden border-t border-white/10 bg-black/90 backdrop-blur-xl"
            data-testid="nav-mobile-menu"
          >
            <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileOpen(false)}
                  className="py-3 text-sm uppercase tracking-[0.2em] text-white/75 hover:text-[#007AFF] font-body border-b border-white/5 last:border-b-0"
                  data-testid={`m-${l.testId}`}
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
